import apiClient, {
  getAuthToken,
  withExternalServiceAuthPolicy,
} from "../api/httpClient";
import endpoints from "../api/endpoints";
import {
  AI_RAG_CHAT_HISTORY_URL,
  AI_RAG_CHAT_NEW_URL,
  AI_RAG_CHAT_URL,
  AI_RAG_CHAT_VOICE_URL,
  AI_RAG_CHAT_VOICE_STREAM_URL,
  AI_VOICE_PROCESS_URL,
  AI_VOICE_SESSION_CONTEXT_URL,
  AI_VOICE_SESSION_RESET_URL,
  BASE_URL,
} from "../config/env";
import { getActiveAiDeviceId } from "../utils/aiDeviceContext";

const trimTrailingSlash = (value) => String(value || "").replace(/\/+$/, "");
const configuredRagTimeoutMs = Number(process.env.REACT_APP_AI_RAG_TIMEOUT_MS);
const AI_RAG_TIMEOUT_MS =
  Number.isFinite(configuredRagTimeoutMs) && configuredRagTimeoutMs > 0
    ? configuredRagTimeoutMs
    : 60000;

const getAudioBaseUrl = () => trimTrailingSlash(BASE_URL);

const getAuthenticatedHeaders = (headers = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error("The authenticated user token is unavailable. Please log in again.");
  }

  return {
    ...headers,
    Authorization: `Bearer ${token}`,
  };
};

export const VOICE_VIDEO_END_BEHAVIOR_OPTIONS = [
  { value: "close", label: "Close popup when video ends" },
  { value: "stay", label: "Keep popup open when video ends" },
  { value: "loop", label: "Replay video when it ends" },
];

export const DEFAULT_AI_CHAT_VOICE_REPLY_CONFIG = {
  voiceReply: true,
  defaultVoice: "alloy",
  voices: [
    { value: "nova", label: "Nova", category: "female" },
    { value: "shimmer", label: "Shimmer", category: "female" },
    { value: "coral", label: "Coral", category: "female" },
    { value: "sage", label: "Sage", category: "female" },
    { value: "onyx", label: "Onyx", category: "male" },
    { value: "echo", label: "Echo", category: "male" },
    { value: "ash", label: "Ash", category: "male" },
    { value: "ballad", label: "Ballad", category: "male" },
    { value: "verse", label: "Verse", category: "male" },
    { value: "alloy", label: "Alloy", category: "neutral" },
    { value: "fable", label: "Fable", category: "neutral" },
  ],
  allowedVoices: [
    "nova", "shimmer", "coral", "sage", "onyx", "echo", "ash", "ballad", "verse", "alloy", "fable",
  ],
  subscriberAllowedVoices: {},
};

const toVoiceLabel = (value) =>
  String(value || "")
    .trim()
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const normalizeVoiceValueList = (items, validValues) => {
  if (!Array.isArray(items)) return null;
  const seen = new Set();
  return items
    .map((item) => String(item || "").trim().toLowerCase())
    .filter((item) => item && validValues.has(item) && !seen.has(item) && seen.add(item));
};

export const normalizeAiChatVoiceReplyConfig = (value = {}) => {
  const source = value && typeof value === "object" ? value : {};
  const rawVoices = Array.isArray(source.voices)
    ? source.voices
    : DEFAULT_AI_CHAT_VOICE_REPLY_CONFIG.voices;
  const seen = new Set();
  const voices = rawVoices
    .map((item) => {
      if (typeof item === "string") {
        const voiceValue = item.trim().toLowerCase();
        return voiceValue
          ? { value: voiceValue, label: toVoiceLabel(voiceValue), category: "custom" }
          : null;
      }
      if (!item || typeof item !== "object") return null;
      const voiceValue = String(item.value || item.voice || item.id || "").trim().toLowerCase();
      if (!voiceValue) return null;
      return {
        value: voiceValue,
        label: String(item.label || toVoiceLabel(voiceValue)).trim() || toVoiceLabel(voiceValue),
        category: String(item.category || item.gender || "custom").trim().toLowerCase() || "custom",
      };
    })
    .filter((voice) => {
      if (!voice || seen.has(voice.value)) return false;
      seen.add(voice.value);
      return true;
    });
  const safeVoices = voices.length
    ? voices
    : DEFAULT_AI_CHAT_VOICE_REPLY_CONFIG.voices.map((voice) => ({ ...voice }));
  const validValues = new Set(safeVoices.map((voice) => voice.value));

  const normalizedAllowed = normalizeVoiceValueList(source.allowedVoices, validValues);
  const allowedVoices = normalizedAllowed?.length
    ? normalizedAllowed
    : safeVoices.map((voice) => voice.value);

  const rawSubscriberAllowed =
    source.subscriberAllowedVoices &&
    typeof source.subscriberAllowedVoices === "object" &&
    !Array.isArray(source.subscriberAllowedVoices)
      ? source.subscriberAllowedVoices
      : {};
  const subscriberAllowedVoices = Object.entries(rawSubscriberAllowed).reduce((acc, [subscriberId, items]) => {
    const normalized = normalizeVoiceValueList(items, validValues);
    if (normalized?.length) acc[String(subscriberId)] = normalized;
    return acc;
  }, {});

  const requestedDefault = String(source.defaultVoice || source.voice || "").trim().toLowerCase();
  const defaultVoice = allowedVoices.includes(requestedDefault)
    ? requestedDefault
    : allowedVoices.includes(DEFAULT_AI_CHAT_VOICE_REPLY_CONFIG.defaultVoice)
      ? DEFAULT_AI_CHAT_VOICE_REPLY_CONFIG.defaultVoice
      : allowedVoices[0];

  return {
    voiceReply: source.voiceReply !== false && source.voice_reply !== false,
    defaultVoice,
    voices: safeVoices,
    allowedVoices,
    subscriberAllowedVoices,
  };
};

export const resolveAiChatVoiceForUser = (config, preferredVoice) => {
  const normalized = normalizeAiChatVoiceReplyConfig(config);
  const availableValues = normalized.voices.map((voice) => voice.value);
  const preferred = String(preferredVoice || "").trim().toLowerCase();
  if (availableValues.includes(preferred)) return preferred;
  if (availableValues.includes(normalized.defaultVoice)) return normalized.defaultVoice;
  return availableValues[0] || "";
};

export const normalizeVoiceVideoEndBehavior = (value) =>
  VOICE_VIDEO_END_BEHAVIOR_OPTIONS.some((option) => option.value === value) ? value : "close";

const readFirstString = (objects, keys) => {
  for (const object of objects) {
    if (!object || typeof object !== "object") continue;

    for (const key of keys) {
      const value = key.split(".").reduce((acc, part) => acc?.[part], object);
      if (typeof value === "string" && value.trim()) {
        return value.trim();
      }
    }
  }

  return "";
};

const readFirstBoolean = (objects, keys) => {
  for (const object of objects) {
    if (!object || typeof object !== "object") continue;

    for (const key of keys) {
      const value = key.split(".").reduce((acc, part) => acc?.[part], object);
      if (typeof value === "boolean") return value;
      if (typeof value === "string" && value.trim()) {
        const normalized = value.trim().toLowerCase();
        if (["true", "1", "yes"].includes(normalized)) return true;
        if (["false", "0", "no"].includes(normalized)) return false;
      }
    }
  }

  return false;
};

const normalizeAudioSource = (value, mimeType) => {
  if (typeof value !== "string" || !value.trim()) return "";

  const cleanValue = value.trim();

  if (/^(https?:|data:|blob:)/i.test(cleanValue)) return cleanValue;

  // MP3 base64 commonly starts with `//...` (for example `//PEx...`).
  // Detect base64 BEFORE relative URLs, otherwise valid AI audio is mistaken
  // for a path and becomes something like https://host//PEx....
  const compact = cleanValue.replace(/\s/g, "");
  const looksLikeBase64 = compact.length > 96 && /^[A-Za-z0-9+/=]+$/.test(compact);

  if (looksLikeBase64) {
    return `data:${mimeType || "audio/mpeg"};base64,${compact}`;
  }

  if (cleanValue.startsWith("/")) {
    return `${getAudioBaseUrl()}${cleanValue}`;
  }

  return "";
};

const looksLikeBase64Payload = (value, minimumLength = 256) => {
  if (typeof value !== "string") return false;
  const compact = value.trim().replace(/\s/g, "");
  return compact.length > minimumLength && /^[A-Za-z0-9+/=]+$/.test(compact);
};

const findNestedAudioSource = (payload, fallbackMimeType = "") => {
  const seen = new Set();
  const queue = [payload];
  while (queue.length) {
    const current = queue.shift();
    if (!current || typeof current !== "object" || seen.has(current)) continue;
    seen.add(current);

    for (const [key, value] of Object.entries(current)) {
      const lowerKey = String(key).toLowerCase();
      if (typeof value === "string" && /(audio|voice|tts)/.test(lowerKey)) {
        const source = normalizeAudioSource(value, fallbackMimeType);
        if (source) return source;
      } else if (value && typeof value === "object") {
        queue.push(value);
      }
    }
  }
  return "";
};

const getAudioExtension = (mimeType) => {
  const type = String(mimeType || "").toLowerCase();
  if (type.includes("mp4")) return "m4a";
  if (type.includes("mpeg") || type.includes("mp3")) return "mp3";
  if (type.includes("wav")) return "wav";
  if (type.includes("ogg")) return "ogg";
  return "webm";
};

const audioFormatToMimeType = (value) => {
  const format = String(value || "").trim().toLowerCase().replace(/^audio\//, "");
  if (!format) return "";
  if (["mp3", "mpeg", "mpeg3"].includes(format)) return "audio/mpeg";
  if (["m4a", "mp4", "aac"].includes(format)) return format === "aac" ? "audio/aac" : "audio/mp4";
  if (["wav", "wave", "x-wav"].includes(format)) return "audio/wav";
  if (["ogg", "opus"].includes(format)) return format === "opus" ? "audio/ogg;codecs=opus" : "audio/ogg";
  if (["webm"].includes(format)) return "audio/webm";
  return value && String(value).includes("/") ? String(value) : `audio/${format}`;
};

const extractAssistantAudio = (payload) => {
  const root = payload && typeof payload === "object" ? payload : {};
  const data = root.data && typeof root.data === "object" ? root.data : root;
  const nested = data.data && typeof data.data === "object" ? data.data : data;
  const objects = [nested, data, root];
  const audioFormat = readFirstString(objects, [
    "audio_format",
    "audioFormat",
    "format",
    "voice_reply.audio_format",
    "tts.audio_format",
  ]);
  const explicitMimeType = readFirstString(objects, [
    "audio_mime_type",
    "audioMimeType",
    "mime_type",
    "voice_reply.mime_type",
    "tts.mime_type",
  ]);
  const audioMimeType = explicitMimeType || audioFormatToMimeType(audioFormat) || "audio/mpeg";
  const audioValue = readFirstString(objects, [
    "audio_base64",
    "response_audio_base64",
    "voice_audio_base64",
    "voice_reply_audio_base64",
    "tts_audio_base64",
    "audio",
    "response_audio",
    "voice_audio",
    "audio_url",
    "response_audio_url",
    "voice_reply.audio_base64",
    "voice_reply.audio",
    "voice_reply.url",
    "tts.audio_base64",
    "tts.audio",
    "tts.url",
  ]);
  const audioSource =
    normalizeAudioSource(audioValue, audioMimeType) ||
    findNestedAudioSource(payload, audioMimeType);
  return { audioSource, audioMimeType, audioFormat };
};



export const normalizeVoiceAssistantResponse = (payload) => {
  if (typeof payload === "string") {
    return {
      transcript: "",
      responseText: payload.trim(),
      sessionId: "",
      needsClarification: false,
      audioSource: "",
      raw: payload,
    };
  }

  const root = payload && typeof payload === "object" ? payload : {};
  const data = root.data && typeof root.data === "object" ? root.data : root;
  const nested = data.data && typeof data.data === "object" ? data.data : data;
  const result = data.result && typeof data.result === "object" ? data.result : {};
  const objects = [nested, result, data, root];

  const transcript = readFirstString(objects, [
    "transcript",
    "user_transcript",
    "user_text",
    "input_text",
    "recognized_text",
    "speech_text",
    "request.transcript",
    "request.text",
  ]);

  const sessionId = readFirstString(objects, ["session_id", "sessionId", "conversation_id"]);
  const audioValue = readFirstString(objects, [
    "audio_url",
    "audioUrl",
    "response_audio_url",
    "audio",
    "audio_base64",
    "response_audio",
  ]);
  const audioMimeType = readFirstString(objects, ["audio_mime_type", "audioMimeType", "mime_type"]);

  const rawResponseText = readFirstString(objects, [
    "assistant_response",
    "assistant_text",
    "response_text",
    "reply",
    "answer",
    "response",
    "output_text",
    "result_text",
    "message",
  ]);
  const normalizedResponseText = rawResponseText.toLowerCase();
  const responseText =
    ["ok", "success"].includes(normalizedResponseText) && (transcript || audioValue)
      ? ""
      : rawResponseText;

  const needsClarification =
    readFirstBoolean(objects, [
      "needs_clarification",
      "requires_clarification",
      "clarification_required",
      "needs_clear",
      "requires_clear",
      "clear_required",
      "need_clear",
    ]) ||
    ["clarification_required", "needs_clarification", "requires_clear"].includes(
      String(readFirstString(objects, ["status", "intent", "type", "stage"])).toLowerCase()
    );
  const candidatesSource =
    objects.find((object) => Array.isArray(object?.candidates))?.candidates || [];
  const candidates = candidatesSource.filter(
    (candidate) => typeof candidate === "string" && candidate.trim()
  );
  const clarificationReason = readFirstString(objects, ["reason", "clarification_reason"]);
  const requestId = readFirstString(objects, ["request_id", "requestId"]);

  return {
    transcript,
    responseText,
    sessionId,
    needsClarification,
    clarification: needsClarification
      ? {
          candidates,
          reason: clarificationReason,
          requestId,
        }
      : null,
    audioSource: normalizeAudioSource(audioValue, audioMimeType),
    raw: payload,
  };
};

const ensureAiUrl = (url, label) => {
  const normalized = String(url || "").trim();
  if (!normalized) {
    throw new Error(`${label} is not configured.`);
  }
  return normalized;
};

const toPositiveInteger = (value) => {
  const normalized = Number(value);
  return Number.isInteger(normalized) && normalized > 0 ? normalized : null;
};

const resolveOptionalDeviceId = (deviceId) => {
  const source = deviceId === undefined ? getActiveAiDeviceId() : deviceId;
  return String(source ?? "").trim();
};

const getDirectAiRequestConfig = ({ signal, json = false } = {}) => withExternalServiceAuthPolicy({
  signal,
  headers: getAuthenticatedHeaders({
    Accept: "application/json",
    ...(json ? { "Content-Type": "application/json" } : {}),
  }),
  timeout: AI_RAG_TIMEOUT_MS,
  // The AI service authenticates with the Sama bearer token. Sending browser
  // cookies cross-origin is unnecessary and would make CORS stricter.
  withCredentials: false,
});

export async function processVoiceCommand({
  audioBlob,
  sessionId,
  organizationId,
  userId,
  deviceId,
  signal,
}) {
  if (!audioBlob) {
    throw new Error("No audio recording was found.");
  }

  const cleanSessionId = String(sessionId || "").trim();
  const orgId = toPositiveInteger(organizationId);
  const authenticatedUserId = toPositiveInteger(userId);

  if (!cleanSessionId) {
    throw new Error("Voice session ID is required.");
  }

  if (!orgId) {
    throw new Error("Choose an organization before using the AI voice assistant.");
  }

  if (!authenticatedUserId) {
    throw new Error("Authenticated user is required for voice commands.");
  }

  const formData = new FormData();
  const fileName = `voice-message.${getAudioExtension(audioBlob.type)}`;

  formData.append("audio", audioBlob, fileName);
  formData.append("session_id", cleanSessionId);
  formData.append("org_id", String(orgId));
  formData.append("user_id", String(authenticatedUserId));
  const cleanDeviceId = resolveOptionalDeviceId(deviceId);
  if (cleanDeviceId) formData.append("device_id", cleanDeviceId);

  const response = await apiClient.post(
    ensureAiUrl(AI_VOICE_PROCESS_URL, "The AI voice-command URL"),
    formData,
    getDirectAiRequestConfig({ signal })
  );

  return normalizeVoiceAssistantResponse(response.data);
}

export async function resetVoiceSession({ sessionId, signal }) {
  const cleanSessionId = String(sessionId || "").trim();
  if (!cleanSessionId) {
    throw new Error("Voice session ID is required.");
  }

  const response = await apiClient.post(
    ensureAiUrl(AI_VOICE_SESSION_RESET_URL, "The AI voice-session reset URL"),
    { session_id: cleanSessionId },
    getDirectAiRequestConfig({ signal, json: true })
  );

  return response.data;
}

export async function fetchVoiceSessionContext({
  sessionId,
  organizationId,
  userId,
  signal,
}) {
  const cleanSessionId = String(sessionId || "").trim();
  const org_id = toPositiveInteger(organizationId);
  const user_id = toPositiveInteger(userId);

  if (!cleanSessionId) throw new Error("Voice session ID is required.");
  if (!org_id) throw new Error("Organization is required.");
  if (!user_id) throw new Error("Authenticated user is required.");

  const response = await apiClient.get(
    ensureAiUrl(AI_VOICE_SESSION_CONTEXT_URL, "The AI voice-session context URL"),
    {
      ...getDirectAiRequestConfig({ signal }),
      params: { session_id: cleanSessionId, user_id, org_id },
    }
  );

  return response.data;
}

export const normalizeAssistantChatResponse = (payload) => {
  if (typeof payload === "string") {
    return {
      text: payload.trim(),
      sources: [],
      success: true,
      sessionId: "",
      audioSource: "",
      audioMimeType: "",
      audioFormat: "",
      voice: "",
      raw: payload,
    };
  }

  const root = payload && typeof payload === "object" ? payload : {};
  const data = root.data && typeof root.data === "object" ? root.data : root;
  const objects = [data, root];
  const { audioSource, audioMimeType, audioFormat } = extractAssistantAudio(payload);
  const text = readFirstString(objects, [
    "data",
    "answer",
    "reply",
    "response",
    "message",
    "text",
    "assistant_response",
    "assistant_text",
    "response_text",
  ]);

  return {
    text: audioSource && looksLikeBase64Payload(text) ? "" : text,
    sources: Array.isArray(data.sources)
      ? data.sources
      : Array.isArray(root.sources)
        ? root.sources
        : [],
    success:
      typeof data.success === "boolean"
        ? data.success
        : typeof root.success === "boolean"
          ? root.success
          : true,
    sessionId: readFirstString(objects, ["session_id", "sessionId"]),
    audioSource,
    audioMimeType,
    audioFormat,
    voice: readFirstString(objects, ["voice", "tts.voice", "voice_reply.voice"]),
    raw: payload,
  };
};

export const normalizeAssistantVoiceChatResponse = (payload) => {
  const base = normalizeAssistantChatResponse(payload);
  const root = payload && typeof payload === "object" ? payload : {};
  const data = root.data && typeof root.data === "object" ? root.data : root;
  const nested = data.data && typeof data.data === "object" ? data.data : data;
  const objects = [nested, data, root];

  return {
    ...base,
    transcript: readFirstString(objects, [
      "transcript",
      "user_transcript",
      "recognized_text",
      "input_text",
    ]),
    stt: data.stt ?? root.stt ?? null,
    voiceCommandContextApplied: readFirstBoolean(
      objects,
      ["voice_command_context_applied", "voiceCommandContextApplied"]
    ),
    resolvedPrompt: readFirstString(
      objects,
      ["resolved_prompt", "resolvedPrompt"]
    ),
  };
};

const getChatScope = ({ subscriberId, organizationId }) => {
  const subid = toPositiveInteger(subscriberId);
  const organization_id = toPositiveInteger(organizationId);

  if (!subid) {
    throw new Error("Authenticated subscriber is required.");
  }

  if (!organization_id) {
    throw new Error("Choose an organization before using chat.");
  }

  return { subid, organization_id };
};

export async function sendAssistantChatMessage({
  prompt,
  sessionId,
  subscriberId,
  organizationId,
  deviceId,
  voiceReply = true,
  voice,
  signal,
}) {
  const cleanPrompt = String(prompt || "").trim();
  const cleanSessionId = String(sessionId || "").trim();
  const { subid, organization_id } = getChatScope({ subscriberId, organizationId });

  if (!cleanPrompt) {
    throw new Error("Write a message before sending.");
  }

  if (!cleanSessionId) {
    throw new Error("Chat session ID is required.");
  }

  const payload = {
    prompt: cleanPrompt,
    subid,
    organization_id,
    session_id: cleanSessionId,
  };
  const cleanDeviceId = resolveOptionalDeviceId(deviceId);
  if (cleanDeviceId) payload.device_id = cleanDeviceId;

  // Voice output is explicit opt-in. When the user disables voice replies we
  // omit every TTS-related field instead of sending voice_reply=false. This
  // keeps the request identical to a normal text RAG request.
  if (voiceReply !== false) {
    payload.voice_reply = true;
    const cleanVoice = String(voice || "").trim().toLowerCase();
    if (cleanVoice) payload.voice = cleanVoice;
  }

  const response = await apiClient.post(
    ensureAiUrl(AI_RAG_CHAT_URL, "The AI chat URL"),
    payload,
    getDirectAiRequestConfig({ signal, json: true })
  );

  return normalizeAssistantChatResponse(response.data);
}

export async function sendAssistantVoiceChatMessage({
  audioBlob,
  sessionId,
  subscriberId,
  organizationId,
  deviceId,
  voiceReply = true,
  voice,
  signal,
}) {
  if (!audioBlob) {
    throw new Error("No audio recording was found.");
  }

  const cleanSessionId = String(sessionId || "").trim();
  const { subid, organization_id } = getChatScope({ subscriberId, organizationId });

  const formData = new FormData();
  const fileName = `voice-chat.${getAudioExtension(audioBlob.type)}`;
  formData.append("audio", audioBlob, fileName);
  formData.append("subid", String(subid));
  formData.append("organization_id", String(organization_id));
  formData.append("session_id", cleanSessionId);
  const cleanDeviceId = resolveOptionalDeviceId(deviceId);
  if (cleanDeviceId) formData.append("device_id", cleanDeviceId);

  // Same rule as text chat: disabled means no TTS fields at all. The audio
  // field above is the user's input recording and is unrelated to reply TTS.
  if (voiceReply !== false) {
    formData.append("voice_reply", "true");
    const cleanVoice = String(voice || "").trim().toLowerCase();
    if (cleanVoice) formData.append("voice", cleanVoice);
  }

  const response = await apiClient.post(
    ensureAiUrl(AI_RAG_CHAT_VOICE_URL, "The AI voice-chat URL"),
    formData,
    getDirectAiRequestConfig({ signal })
  );

  return normalizeAssistantVoiceChatResponse(response.data);
}


const decodeBase64Chunk = (value) => {
  const cleanValue = String(value || "").trim().replace(/\s/g, "");
  if (!cleanValue || typeof window === "undefined" || typeof window.atob !== "function") {
    return null;
  }

  const binary = window.atob(cleanValue);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

const createStreamAudioSource = (audioChunks = []) => {
  if (typeof window === "undefined" || typeof window.URL?.createObjectURL !== "function") {
    return { audioSource: "", audioMimeType: "", audioFormat: "" };
  }

  const validChunks = audioChunks
    .map((chunk) => ({
      bytes: decodeBase64Chunk(chunk?.audio_base64),
      format: String(chunk?.audio_format || "").trim(),
    }))
    .filter((chunk) => chunk.bytes?.length);

  if (!validChunks.length) {
    return { audioSource: "", audioMimeType: "", audioFormat: "" };
  }

  const audioFormat = validChunks.find((chunk) => chunk.format)?.format || "mp3";
  const audioMimeType = audioFormatToMimeType(audioFormat) || "audio/mpeg";
  const blob = new Blob(validChunks.map((chunk) => chunk.bytes), { type: audioMimeType });

  return {
    audioSource: window.URL.createObjectURL(blob),
    audioMimeType,
    audioFormat,
  };
};

const createStreamError = (payload = {}) => {
  const message = String(payload?.error || payload?.message || "AI voice stream failed.").trim();
  const error = new Error(message || "AI voice stream failed.");
  error.code = payload?.error_code || payload?.code || "AI_VOICE_STREAM_ERROR";
  error.response = {
    status: Number(payload?.status) || 500,
    data: {
      ...payload,
      message: message || "AI voice stream failed.",
    },
  };
  return error;
};

const parseSseEventBlock = (block) => {
  const lines = String(block || "").split(/\r?\n/);
  let eventName = "message";
  const dataLines = [];

  for (const line of lines) {
    if (!line || line.startsWith(":")) continue;
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim() || "message";
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    }
  }

  const rawData = dataLines.join("\n");
  if (!rawData) return { eventName, data: null };

  try {
    return { eventName, data: JSON.parse(rawData) };
  } catch (_error) {
    return { eventName, data: rawData };
  }
};

export async function sendAssistantVoiceChatStreamMessage({
  audioBlob,
  sessionId,
  subscriberId,
  organizationId,
  deviceId,
  voiceReply = true,
  voice,
  signal,
}) {
  if (!audioBlob) {
    throw new Error("No audio recording was found.");
  }

  const cleanSessionId = String(sessionId || "").trim();
  const { subid, organization_id } = getChatScope({ subscriberId, organizationId });

  const formData = new FormData();
  const fileName = `voice-chat.${getAudioExtension(audioBlob.type)}`;
  formData.append("audio", audioBlob, fileName);
  formData.append("subid", String(subid));
  formData.append("organization_id", String(organization_id));
  formData.append("session_id", cleanSessionId);


  const cleanDeviceId = resolveOptionalDeviceId(deviceId);
  if (cleanDeviceId) formData.append("device_id", cleanDeviceId);

  if (voiceReply !== false) {
    formData.append("voice_reply", "true");
    const cleanVoice = String(voice || "").trim().toLowerCase();
    if (cleanVoice) formData.append("voice", cleanVoice);
  }

  const response = await fetch(
    ensureAiUrl(AI_RAG_CHAT_VOICE_STREAM_URL, "The AI streaming voice-chat URL"),
    {
      method: "POST",
      headers: getAuthenticatedHeaders({ Accept: "text/event-stream" }),
      body: formData,
      signal,
      credentials: "omit",
    }
  );

  if (!response.ok) {
    let payload = {};
    try {
      payload = await response.json();
    } catch (_error) {
      payload = { error: `AI voice stream request failed with status ${response.status}.` };
    }
    throw createStreamError({ ...payload, status: response.status });
  }

  if (!response.body?.getReader) {
    throw new Error("Streaming responses are not supported by this browser.");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let transcriptPayload = null;
  let answerPayload = null;
  const audioChunks = [];

  const handleEvent = ({ eventName, data }) => {
    if (!data) return;

    if (eventName === "error") {
      throw createStreamError(typeof data === "object" ? data : { error: String(data) });
    }

    if (eventName === "transcript") {
      transcriptPayload = typeof data === "object" ? data : { transcript: String(data) };
      return;
    }

    if (eventName === "answer") {
      answerPayload = typeof data === "object" ? data : { data: String(data) };
      return;
    }

    if (eventName === "audio_chunk" && typeof data === "object") {
      audioChunks.push(data);
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });

    const blocks = buffer.split(/\r?\n\r?\n/);
    buffer = blocks.pop() || "";
    blocks.forEach((block) => handleEvent(parseSseEventBlock(block)));

    if (done) break;
  }

  if (buffer.trim()) {
    handleEvent(parseSseEventBlock(buffer));
  }

  const normalized = normalizeAssistantVoiceChatResponse(answerPayload || {});
  const streamedAudio = createStreamAudioSource(audioChunks);

  return {
    ...normalized,
    transcript: String(transcriptPayload?.transcript || normalized.transcript || "").trim(),
    stt:
      normalized.stt ||
      (transcriptPayload
        ? {
            language: transcriptPayload.language,
            confidence: transcriptPayload.confidence,
          }
        : null),
    audioSource: streamedAudio.audioSource || normalized.audioSource,
    audioMimeType: streamedAudio.audioMimeType || normalized.audioMimeType,
    audioFormat: streamedAudio.audioFormat || normalized.audioFormat,
  };
}

export const normalizeAssistantChatHistoryResponse = (payload) => {
  const root = payload && typeof payload === "object" ? payload : {};
  const data = root.data && typeof root.data === "object" ? root.data : root;
  const history = Array.isArray(data.history)
    ? data.history
    : Array.isArray(root.history)
      ? root.history
      : [];

  return {
    success:
      typeof data.success === "boolean"
        ? data.success
        : typeof root.success === "boolean"
          ? root.success
          : true,
    sessionId: readFirstString([data, root], ["session_id", "sessionId"]),
    history,
    raw: payload,
  };
};

export async function fetchAssistantChatHistory({
  sessionId,
  subscriberId,
  organizationId,
  limit = 200,
  signal,
}) {
  const cleanSessionId = String(sessionId || "").trim();
  const { subid, organization_id } = getChatScope({ subscriberId, organizationId });
  const parsedLimit = Number(limit);
  const safeLimit = Number.isInteger(parsedLimit)
    ? Math.min(500, Math.max(1, parsedLimit))
    : 200;

  const response = await apiClient.get(
    ensureAiUrl(AI_RAG_CHAT_HISTORY_URL, "The AI chat history URL"),
    {
      ...getDirectAiRequestConfig({ signal }),
      params: {
        ...(cleanSessionId ? { session_id: cleanSessionId } : {}),
        subid,
        organization_id,
        limit: safeLimit,
      },
    }
  );

  return normalizeAssistantChatHistoryResponse(response.data);
}

export async function startNewAssistantChat({
  sessionId,
  subscriberId,
  organizationId,
  signal,
}) {
  const cleanSessionId = String(sessionId || "").trim();
  const { subid, organization_id } = getChatScope({ subscriberId, organizationId });

  const response = await apiClient.post(
    ensureAiUrl(AI_RAG_CHAT_NEW_URL, "The AI new-chat URL"),
    {
      session_id: cleanSessionId,
      subid,
      organization_id,
    },
    getDirectAiRequestConfig({ signal, json: true })
  );

  const root = response.data && typeof response.data === "object" ? response.data : {};
  const data = root.data && typeof root.data === "object" ? root.data : root;

  return {
    success:
      typeof data.success === "boolean"
        ? data.success
        : typeof root.success === "boolean"
          ? root.success
          : true,
    sessionId: readFirstString([data, root], ["session_id", "sessionId"]),
    raw: response.data,
  };
}

export async function deleteAssistantChat({
  sessionId,
  subscriberId,
  organizationId,
  signal,
}) {
  const cleanSessionId = String(sessionId || "").trim();
  const { subid, organization_id } = getChatScope({ subscriberId, organizationId });
  if (!cleanSessionId) throw new Error("Chat session ID is required.");

  const response = await apiClient.delete(
    ensureAiUrl(AI_RAG_CHAT_HISTORY_URL, "The AI chat history URL"),
    {
      ...getDirectAiRequestConfig({ signal, json: true }),
      data: {
        session_id: cleanSessionId,
        subid,
        organization_id,
      },
    }
  );

  return response.data;
}

export const AI_CHAT_VOICE_CONFIG_UPDATED_EVENT = "sama-ai-chat-voice-config-updated";

export const notifyAiChatVoiceConfigUpdated = (value = {}) => {
  const nextConfig = normalizeAiChatVoiceReplyConfig(value);
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new CustomEvent(AI_CHAT_VOICE_CONFIG_UPDATED_EVENT, {
      detail: nextConfig,
    }));
  }
  return nextConfig;
};

export const BROWSER_VOICE_COMMAND_CONFIG_UPDATED_EVENT = "sama-browser-voice-command-config-updated";

export const normalizeVoiceCommandConfig = (value = {}) => {
  const source = value && typeof value === "object" ? value : {};
  const normalizeBasicAction = (action, index) => {
    const sourceAction = action && typeof action === "object" ? action : {};
    const rawAction = sourceAction.action && typeof sourceAction.action === "object"
      ? sourceAction.action
      : { type: "media", media: {} };
    const media = rawAction.media && typeof rawAction.media === "object" ? rawAction.media : {};

    return {
      ...sourceAction,
      id: String(sourceAction.id || `basic-${index + 1}`),
      label: String(sourceAction.label || `Command ${index + 1}`),
      phrases: Array.isArray(sourceAction.phrases) && sourceAction.phrases.length
        ? sourceAction.phrases
        : ["hello sama", "hey sama", "مرحبا سما"],
      action: rawAction.type === "navigate"
        ? {
            ...rawAction,
            type: "navigate",
            path: String(rawAction.path || "/"),
          }
        : {
            ...rawAction,
            type: "media",
            media: {
              ...media,
              path: String(media.path || rawAction.path || ""),
              title: String(media.title || ""),
              mediaType: ["image", "video", "file"].includes(media.mediaType) ? media.mediaType : "image",
              videoEndBehavior: normalizeVoiceVideoEndBehavior(media.videoEndBehavior),
            },
          },
    };
  };
  const subscriberBasicActions =
    source.subscriberBasicActions && typeof source.subscriberBasicActions === "object" && !Array.isArray(source.subscriberBasicActions)
      ? Object.entries(source.subscriberBasicActions).reduce((acc, [subscriberId, actions]) => {
          if (Array.isArray(actions)) acc[String(subscriberId)] = actions.map(normalizeBasicAction);
          return acc;
        }, {})
      : {};

  const requestedLiveVoice = String(source.liveConversation?.voice || "nova").trim().toLowerCase();
  const liveVoice = /^[a-z0-9_-]{1,32}$/.test(requestedLiveVoice) ? requestedLiveVoice : "nova";
  const defaultStopPhrases = [
    "stop sama",
    "end sama",
    "goodbye sama",
    "turn off sama",
    "توقف سما",
    "اوقف سما",
    "أوقف سما",
    "اغلق سما",
    "أغلق سما",
    "انهي سما",
    "أنهي سما",
  ];
  const liveStopPhrases = Array.from(new Set(
    (Array.isArray(source.liveConversation?.stopPhrases) && source.liveConversation.stopPhrases.length
      ? source.liveConversation.stopPhrases
      : defaultStopPhrases)
      .map((item) => String(item || "").trim().slice(0, 120))
      .filter(Boolean)
  )).slice(0, 32);
  const requestedEndOfTurnSilenceMs = Number(source.liveConversation?.endOfTurnSilenceMs);
  const liveEndOfTurnSilenceMs = Number.isFinite(requestedEndOfTurnSilenceMs)
    ? Math.min(10000, Math.max(0, Math.round(requestedEndOfTurnSilenceMs)))
    : 2000;

  return {
    enabled: source.enabled !== false,
    mode: source.mode === "advanced" ? "advanced" : "basic",
    wakeWords: Array.isArray(source.wakeWords) && source.wakeWords.length ? source.wakeWords : ["sama"],
    languages: Array.isArray(source.languages) && source.languages.length ? source.languages : ["en-US", "ar-SA"],
    liveConversation: {
      enabled: source.liveConversation?.enabled !== false,
      voice: liveVoice,
      endOfTurnSilenceMs: liveEndOfTurnSilenceMs,
      stopPhrases: liveStopPhrases,
    },
    deniedMessage:
      source.deniedMessage ||
      "Sorry, we can't proceed your request since you don't have the necessary permission.",
    unknownMessage:
      source.unknownMessage ||
      "Sorry, we can't proceed your request now, please contact us to describe your issue.",
    basicActions: Array.isArray(source.basicActions) ? source.basicActions.map(normalizeBasicAction) : [],
    subscriberBasicActions,
    advanced: {
      pages: Array.isArray(source.advanced?.pages) ? source.advanced.pages : [],
      entities: Array.isArray(source.advanced?.entities) ? source.advanced.entities : [],
    },
  };
};

export const notifyBrowserVoiceCommandConfigUpdated = (value = {}) => {
  const nextConfig = normalizeVoiceCommandConfig(value);

  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new CustomEvent(BROWSER_VOICE_COMMAND_CONFIG_UPDATED_EVENT, {
      detail: nextConfig,
    }));
  }

  return nextConfig;
};


export async function fetchAiChatVoiceReplyConfig(organizationId) {
  const response = await apiClient.get(endpoints.configuration.public(), {
    params: organizationId ? { organization_id: organizationId } : undefined,
    withCredentials: true,
  });
  return normalizeAiChatVoiceReplyConfig(response.data?.data?.aiChatVoiceReply);
}

export async function saveAiChatVoicePreference({ voice, voiceReplyEnabled, signal }) {
  const payload = {};
  const cleanVoice = String(voice || "").trim().toLowerCase();
  if (cleanVoice) payload.voice = cleanVoice;
  if (typeof voiceReplyEnabled === "boolean") {
    payload.voice_reply_enabled = voiceReplyEnabled;
  }

  if (!Object.keys(payload).length) {
    throw new Error("Choose a voice preference first.");
  }

  const response = await apiClient.put(
    endpoints.user.aiChatVoicePreference(),
    payload,
    { ...getDirectAiRequestConfig({ signal, json: true }), withCredentials: true }
  );
  return response.data;
}

export async function fetchVoiceCommandConfig(organizationId) {
  const response = await apiClient.get(endpoints.configuration.public(), {
    params: organizationId ? { organization_id: organizationId } : undefined,
    withCredentials: true,
  });
  return normalizeVoiceCommandConfig(response.data?.data?.aiVoiceCommands);
}

export async function resolveTextVoiceCommand({ transcript, organizationId, signal }) {
  const response = await apiClient.post(
    endpoints.aiVoice.resolveText(),
    {
      transcript,
      organization_id: organizationId,
    },
    {
      signal,
      withCredentials: true,
      headers: { Accept: "application/json" },
    }
  );

  return response.data?.data || response.data;
}

export async function fetchVoiceMediaLibrary() {
  const response = await apiClient.get(endpoints.aiVoice.mediaLibrary(), {
    withCredentials: true,
  });
  return Array.isArray(response.data?.data) ? response.data.data : [];
}

export async function uploadVoiceMedia({ file, title }) {
  const formData = new FormData();
  formData.append("file", file);
  if (title) formData.append("title", title);

  const response = await apiClient.post(endpoints.aiVoice.uploadMedia(), formData, {
    withCredentials: true,
    headers: { Accept: "application/json" },
  });

  return response.data?.data;
}
